import { useState, useRef, useEffect, useCallback } from "react";
import { Gift, RefreshCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const ALL_PRIZES = [
  { text: "10€ OFFERTS", subtitle: "sur votre prochain achat", icon: "gift" },
  { text: "20% DE REMISE", subtitle: "valable 7 jours", icon: "percent" },
  { text: "LIVRAISON OFFERTE", subtitle: "sans minimum d'achat", icon: "truck" },
  { text: "CADEAU SURPRISE", subtitle: "à découvrir en boutique", icon: "sparkle" },
];

const ScratchCard = () => {
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchPercent, setScratchPercent] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [prize, setPrize] = useState(() => ALL_PRIZES[Math.floor(Math.random() * ALL_PRIZES.length)]);
  const [isInitialized, setIsInitialized] = useState(false);

  const cardWidth = 300;
  const cardHeight = 180;

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // Base layer
    const gradient = ctx.createLinearGradient(0, 0, cardWidth, cardHeight);
    gradient.addColorStop(0, "#8C9D78");
    gradient.addColorStop(0.5, "#7A8B69");
    gradient.addColorStop(1, "#6B7C5A");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, cardWidth, cardHeight);

    // Diagonal pattern
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    for (let i = -cardHeight; i < cardWidth + cardHeight; i += 12) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + cardHeight, cardHeight);
      ctx.stroke();
    }

    // Shimmer dots
    for (let i = 0; i < 60; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * cardWidth, Math.random() * cardHeight, Math.random() * 2 + 0.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.3})`;
      ctx.fill();
    }

    // Text
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "bold 18px system-ui";
    ctx.textAlign = "center";
    ctx.fillText("GRATTEZ POUR GAGNER", cardWidth / 2, cardHeight / 2 - 8);

    ctx.font = "13px system-ui";
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.fillText("Utilisez votre souris ou votre doigt", cardWidth / 2, cardHeight / 2 + 16);

    // Border coins arrondis simulés
    ctx.strokeStyle = "rgba(255,255,255,0.3)";
    ctx.lineWidth = 2;
    ctx.strokeRect(8, 8, cardWidth - 16, cardHeight - 16);

    setIsInitialized(true);
  }, []);

  useEffect(() => {
    const timer = setTimeout(initCanvas, 100);
    return () => clearTimeout(timer);
  }, [initCanvas]);

  const scratch = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      if (!canvas || revealed) return;
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      const scaleX = cardWidth / rect.width;
      const scaleY = cardHeight / rect.height;

      const clientX = e.clientX || e.touches?.[0]?.clientX;
      const clientY = e.clientY || e.touches?.[0]?.clientY;
      if (!clientX || !clientY) return;

      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;

      ctx.globalCompositeOperation = "destination-out";

      // Main circle
      ctx.beginPath();
      ctx.arc(x, y, 22, 0, Math.PI * 2);
      ctx.fill();

      // Soft edges
      const grad = ctx.createRadialGradient(x, y, 15, x, y, 30);
      grad.addColorStop(0, "rgba(0,0,0,1)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, 30, 0, Math.PI * 2);
      ctx.fill();

      // Calculate scratch percentage
      const imageData = ctx.getImageData(0, 0, cardWidth, cardHeight);
      let transparentPixels = 0;
      const totalPixels = imageData.data.length / 4;
      for (let i = 3; i < imageData.data.length; i += 4) {
        if (imageData.data[i] < 128) transparentPixels++;
      }
      const percent = Math.round((transparentPixels / totalPixels) * 100);
      setScratchPercent(percent);

      if (percent > 55) {
        setRevealed(true);
      }
    },
    [revealed]
  );

  const reset = () => {
    setRevealed(false);
    setScratchPercent(0);
    setIsInitialized(false);
    setPrize(ALL_PRIZES[Math.floor(Math.random() * ALL_PRIZES.length)]);
    setTimeout(initCanvas, 50);
  };

  return (
    <div className="text-center" data-testid="scratch-card">
      {/* Progress bar */}
      <div className="max-w-[300px] mx-auto mb-4">
        <div className="flex justify-between text-xs text-stone-400 mb-1">
          <span>Progression</span>
          <span>{scratchPercent}%</span>
        </div>
        <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#8C9D78] to-[#C65D47] rounded-full transition-all duration-300"
            style={{ width: `${scratchPercent}%` }}
          />
        </div>
      </div>

      <div className="relative inline-block mb-5">
        {/* Shadow effect */}
        <div className="absolute inset-2 bg-black/10 rounded-2xl blur-md translate-y-2" />

        {/* Prize card underneath */}
        <div
          className="relative rounded-2xl overflow-hidden"
          style={{ width: cardWidth, height: cardHeight }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#1D3557] via-[#1D3557] to-[#2A4A6D]" />
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            {prize.icon === "gift" && <Gift className="w-10 h-10 text-[#FFD700] mb-2" />}
            {prize.icon === "percent" && (
              <div className="w-10 h-10 rounded-full bg-[#C65D47] flex items-center justify-center mb-2">
                <span className="text-white font-bold text-lg">%</span>
              </div>
            )}
            {prize.icon === "truck" && (
              <div className="w-10 h-10 rounded-full bg-[#8C9D78] flex items-center justify-center mb-2">
                <span className="text-white text-lg">📦</span>
              </div>
            )}
            {prize.icon === "sparkle" && <Sparkles className="w-10 h-10 text-[#FFD700] mb-2" />}
            <p className="text-white font-bold text-xl tracking-wide">{prize.text}</p>
            <p className="text-white/60 text-sm mt-1">{prize.subtitle}</p>
          </div>
          {/* Star decorations */}
          <div className="absolute top-3 left-3 w-2 h-2 bg-[#FFD700] rounded-full opacity-40" />
          <div className="absolute top-3 right-3 w-2 h-2 bg-[#FFD700] rounded-full opacity-40" />
          <div className="absolute bottom-3 left-3 w-2 h-2 bg-[#FFD700] rounded-full opacity-40" />
          <div className="absolute bottom-3 right-3 w-2 h-2 bg-[#FFD700] rounded-full opacity-40" />
        </div>

        {/* Scratch canvas overlay - single element to preserve drawing */}
        {!revealed && (
          <canvas
            ref={canvasRef}
            width={cardWidth}
            height={cardHeight}
            className={`absolute inset-0 rounded-2xl ${isInitialized ? "cursor-pointer" : ""}`}
            style={{ touchAction: "none" }}
            onMouseDown={() => isInitialized && setIsScratching(true)}
            onMouseUp={() => setIsScratching(false)}
            onMouseLeave={() => setIsScratching(false)}
            onMouseMove={(e) => isInitialized && isScratching && scratch(e)}
            onTouchStart={() => isInitialized && setIsScratching(true)}
            onTouchEnd={() => setIsScratching(false)}
            onTouchMove={(e) => {
              if (!isInitialized) return;
              e.preventDefault();
              scratch(e);
            }}
            data-testid="scratch-canvas"
          />
        )}
      </div>

      {/* Win message */}
      {revealed && (
        <div className="mb-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#8C9D78]/20 to-[#C65D47]/20 rounded-full border border-[#8C9D78]/30">
            <Sparkles className="w-5 h-5 text-[#C65D47]" />
            <p className="text-[#1D3557] font-bold">Félicitations ! Vous avez gagné !</p>
          </div>
        </div>
      )}

      <Button
        onClick={reset}
        variant="outline"
        className="rounded-full px-8 h-10 hover:bg-[#8C9D78]/10 border-[#8C9D78]/30"
        data-testid="scratch-reset-btn"
      >
        <RefreshCcw className="w-4 h-4 mr-2" />
        Nouvelle carte
      </Button>
    </div>
  );
};

export default ScratchCard;
